import { productEvent, ProductJourney } from "@shared/analytics/productEvents";
import { FEATURE_FLAGS } from "@solomon-apps/config";
import {
    formatCurrency,
    type YaasPositionCardData
} from "@solomon-apps/shared";
import {
    ArrowShortUpRightIcon,
    PencilPaperIcon,
    RouteIcon
} from "@solomon-labs/design/components/icons";
import {
    ButtonIcon,
    Button as DsButton,
    Text
} from "@solomon-labs/design/components/ui";
import { AnimatePresence, motion } from "motion/react";
import React, { useRef } from "react";

import type { RaydiumLpHolding } from "@/hooks/yaas/lpTypes";

import { InlineDestinationEditor } from "../application/InlineDestinationEditor";
import { balanceListRows } from "../helpers/balanceListRows";
import {
    breakdownLogoClassFor,
    breakdownTitleFor,
    shortAddress
} from "../helpers/breakdownDisplay";
import { breakdownIconFor } from "../helpers/breakdownIcon";
import {
    formatRoutingLabel,
    resolveEffectiveRouting,
    routingLinkAddress
} from "../helpers/effectiveRouting";
import { isRetailSelfHeldPosition } from "../helpers/isRetailSelfHeldPosition";
import { sortByAmountDesc } from "../helpers/sortByAmount";

import { VenuesGhostRow } from "./VenuesGhostRow";

interface PositionsBreakdownListProps {
    positions: YaasPositionCardData[];
    balanceHoldings?: RaydiumLpHolding[];
    /** The wallet summary directly above already draws the shared row rule. */
    followsWalletRow?: boolean;
    /** Connected wallet — drives the "Same Wallet" badge when a
     *  position's destination matches. */
    connectedWalletAddress: string;
    advancedMode: boolean;
    /** Whether the Update affordance can be opened at all. Local UI,
     *  so demo/sim frames get this. */
    canEditDestinations: boolean;
    /** Whether an opened editor may actually POST. False in demo/sim
     *  frames — the editor opens and is inspectable, but cannot write. */
    canSubmitDestinations: boolean;
    inlineEditPosition: string | null;
    /** Toggles the Mode-2 inline editor open/closed for the row id. */
    setInlineEditPosition: React.Dispatch<React.SetStateAction<string | null>>;
    /** Mode-3 routing surface — opens the Reward Destinations panel
     *  scoped to the clicked row's position. */
    setRoutingPositionSource: (
        positionKey: string,
        analytics?: ProductJourney
    ) => void;
    /** DRAFT — renders the "not yet" venues row below the real rows.
     *  Omit to suppress it entirely (sim consumers, below-minimum). */
    onOpenVenues?: () => void;
    /** Base APY quoted in the ghost row's promise. */
    baseApy?: number;
}

/**
 * Per-position list (Account Balance row, dest wallet, Update). Shared
 * between two render paths:
 *   1. PositionsSection (page-reload steady state): direct render below the
 *      flat YaasPositionCard.
 *   2. real Program Position views
 *      completes): rendered below the persistent form panel so the
 *      page no longer requires a hard refresh to surface this list.
 *
 * Visibility gating (the below-minimum state) is the caller's
 * responsibility — both paths suppress the list in those states for
 * the same reason (the in-card explainers take priority).
 */
export function PositionsBreakdownList({
    positions,
    balanceHoldings = [],
    followsWalletRow = false,
    connectedWalletAddress,
    advancedMode,
    canEditDestinations,
    canSubmitDestinations,
    inlineEditPosition,
    setInlineEditPosition,
    setRoutingPositionSource,
    onOpenVenues,
    baseApy
}: PositionsBreakdownListProps): React.ReactElement {
    const editorJourney = useRef<ProductJourney | undefined>(undefined);
    const renderPositionRow = (
        position: YaasPositionCardData,
        index: number
    ): React.ReactElement => {
        const positionKey = String(position.id);

        // Effective routing = splits if present, else the single
        // destination (see helpers/effectiveRouting). Per intent doc
        // (retail-yaas.md §Display rule), a single destination equal to
        // the connected wallet shows "Same Wallet" rather than echoing
        // the user's own address back at them.
        const routing = resolveEffectiveRouting(
            position,
            connectedWalletAddress
        );
        const destinationDisplay = formatRoutingLabel(routing);
        // Null only when rewards split across >1 destination —
        // there is no single account to open, so the label stays
        // plain text rather than linking to an arbitrary split.
        const destinationLink = routingLinkAddress(routing);
        // The row title links out to Solscan in two cases: an LP
        // row opens its pool, and the wallet row is the wallet's
        // own address. Other types stay plain: the API falls back
        // to the mint or the wallet's address for them, which is
        // not what the row names.
        const isWalletRow = isRetailSelfHeldPosition(position.pool);
        const titleLink =
            isWalletRow && connectedWalletAddress
                ? connectedWalletAddress
                : position.pool.type === "DEX LP" && position.position.lpAddress
                  ? position.position.lpAddress
                  : null;
        const title =
            isWalletRow && connectedWalletAddress
                ? shortAddress(connectedWalletAddress)
                : breakdownTitleFor(
                      position.pool,
                      position.position.lpIdentity?.protocol
                  );
        return (
            <BalanceRow
                key={positionKey}
                icon={
                    breakdownIconFor(position.pool) ?? (
                        <div
                            className={`size-8 rounded-full shrink-0 ${breakdownLogoClassFor(index)}`}
                            aria-hidden
                        />
                    )
                }
                title={title}
                titleLink={titleLink}
                linkType={isWalletRow ? "wallet_explorer" : "pool_explorer"}
                amount={position.position.formattedUSDvValue}
                destination={
                    FEATURE_FLAGS.retailEnrollmentDestination && (
                        <div className="flex items-center gap-2 min-w-0">
                            <Text
                                variant="small"
                                render={<span />}
                                className="text-foreground-muted shrink-0"
                            >
                                Destination:
                            </Text>
                            {destinationLink ? (
                                <DsButton
                                    variant="link"
                                    size="sm"
                                    trailing={<ArrowShortUpRightIcon />}
                                    // Base UI assumes `render`
                                    // supplies a native <button>
                                    // and logs an error every
                                    // render otherwise. Saying it
                                    // is not one also makes it
                                    // treat the element as a link
                                    // for keyboard handling — but
                                    // it then stamps
                                    // `role="button"`, which would
                                    // announce this link as a
                                    // button. `role` merges last,
                                    // so naming the role the
                                    // anchor already has puts it
                                    // back. Neither prop is in
                                    // button.intent.md.
                                    nativeButton={false}
                                    role="link"
                                    render={
                                        <a
                                            href={`https://solscan.io/account/${destinationLink}`}
                                            onClick={() =>
                                                productEvent(
                                                    "rewards:link_clicked",
                                                    {
                                                        entry_point:
                                                            "position_row",
                                                        link_type:
                                                            "destination_explorer"
                                                    }
                                                )
                                            }
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        />
                                    }
                                >
                                    {destinationDisplay}
                                </DsButton>
                            ) : (
                                <Text
                                    variant="small"
                                    render={<span />}
                                    className="text-foreground-muted tabular-nums min-w-0 truncate"
                                >
                                    {destinationDisplay}
                                </Text>
                            )}
                            {/* Routing editors are feature-
                                        gated (endpoints removed from
                                        yaas-api; data returns via
                                        Jachin) — hide the affordance
                                        entirely when off, keep the
                                        disabled treatment for
                                        sim/demo states when on. */}
                            {FEATURE_FLAGS.retailPositionRouting && (
                                <ButtonIcon
                                    // Icon-only and a real
                                    // button, so this is
                                    // ButtonIcon's case rather
                                    // than Button's. `transparent`
                                    // is the closest type to what
                                    // this was; the default is
                                    // `inverse-raised`, which
                                    // would put a chip in a row
                                    // that has no other surface.
                                    // `lg` keeps the glyph at the
                                    // 1.25rem it already was.
                                    variant="transparent"
                                    size="lg"
                                    aria-label={
                                        advancedMode
                                            ? `Edit rewards routing for ${title}`
                                            : `Edit rewards destination for ${title}`
                                    }
                                    disabled={!canEditDestinations}
                                    onClick={() => {
                                        if (!canEditDestinations) {
                                            return;
                                        }
                                        if (
                                            !advancedMode &&
                                            inlineEditPosition === positionKey
                                        ) {
                                            editorJourney.current?.close(
                                                "toggle"
                                            );
                                            setInlineEditPosition(null);
                                            return;
                                        }
                                        editorJourney.current?.close(
                                            "another_position"
                                        );
                                        const journey = new ProductJourney(
                                            advancedMode
                                                ? "rewards_routing"
                                                : "rewards_destination",
                                            "position_row",
                                            connectedWalletAddress
                                        );
                                        journey.send("requested");
                                        editorJourney.current = journey;
                                        if (advancedMode)
                                            setRoutingPositionSource(
                                                positionKey,
                                                journey
                                            );
                                        else setInlineEditPosition(positionKey);
                                    }}
                                    // The affordance does two
                                    // different jobs — a pencil
                                    // opens the inline single-
                                    // destination editor, the
                                    // route mark opens the
                                    // split-routing panel.
                                    // Swapping the glyph is what
                                    // tells the user which mode
                                    // they are in, since the mode
                                    // is set from a link further
                                    // down the page. ButtonIcon
                                    // takes the glyph as `icon`,
                                    // not as children, and its
                                    // `size` owns the dimension —
                                    // hence no `size-5` here.
                                    icon={
                                        advancedMode ? (
                                            <RouteIcon />
                                        ) : (
                                            <PencilPaperIcon />
                                        )
                                    }
                                />
                            )}
                        </div>
                    )
                }
                editor={
                    <AnimatePresence initial={false}>
                        {FEATURE_FLAGS.retailPositionRouting &&
                            inlineEditPosition === positionKey &&
                            !advancedMode && (
                                <motion.div
                                    key="inline-editor"
                                    initial={{
                                        height: 0,
                                        opacity: 0
                                    }}
                                    animate={{
                                        height: "auto",
                                        opacity: 1
                                    }}
                                    exit={{
                                        height: 0,
                                        opacity: 0
                                    }}
                                    transition={{
                                        duration: 0.25,
                                        ease: "easeOut"
                                    }}
                                    className="overflow-hidden"
                                >
                                    <InlineDestinationEditor
                                        analytics={editorJourney.current}
                                        connectedWallet={connectedWalletAddress}
                                        walletAddress={connectedWalletAddress}
                                        canSubmit={canSubmitDestinations}
                                        onClose={() =>
                                            setInlineEditPosition(null)
                                        }
                                    />
                                </motion.div>
                            )}
                    </AnimatePresence>
                }
            />
        );
    };
    return (
        <div
            className={
                followsWalletRow
                    ? "flex flex-col"
                    : "flex flex-col mt-2 md:mt-4 border-t border-line-neutral"
            }
        >
            {/* Every row, largest first: yaas-api positions (the wallet
                row among them) and discovered LP pools share one
                ordering, so the biggest holding leads whatever its
                source. Unknown amounts sort last. */}
            {sortByAmountDesc(
                // One row per pool: several price-range positions in the
                // same pool sum into it (see balanceListRows).
                balanceListRows(positions, balanceHoldings).map(
                    (row, index) => ({
                        amount: row.amount,
                        node:
                            row.kind === "position" ? (
                                renderPositionRow(row.position, index)
                            ) : (
                                <BalanceRow
                                    key={row.key}
                                    icon={breakdownIconFor(row.iconPool)}
                                    title={row.title}
                                    titleLink={row.poolAddress}
                                    linkType="pool_explorer"
                                    amount={
                                        row.amount === null
                                            ? "—"
                                            : formatCurrency(row.amount)
                                    }
                                />
                            )
                    })
                )
            ).map((row) => row.node)}
            {/* The enticement toward `/venues`. Gated on the same flag
                as the route and the nav item — with `venues` off the
                page it links to is not registered, so advertising a
                combined rate here would dead-end on NotFoundPage. */}
            {FEATURE_FLAGS.venues && onOpenVenues && baseApy !== undefined && (
                <VenuesGhostRow baseApy={baseApy} onOpenVenues={onOpenVenues} />
            )}
        </div>
    );
}

interface BalanceRowProps {
    icon: React.ReactNode;
    title: string;
    titleLink: string | null;
    linkType: "wallet_explorer" | "pool_explorer";
    amount: string;
    destination?: React.ReactNode;
    editor?: React.ReactNode;
}

/** Shared presentation; only backend positions supply routing controls. */
function BalanceRow({
    icon,
    title,
    titleLink,
    linkType,
    amount,
    destination,
    editor
}: BalanceRowProps): React.ReactElement {
    return (
        <div className="px-2 py-2 border-b border-line-neutral">
            <div className="flex flex-col">
                <div className="flex items-center gap-3 min-w-0 w-full">
                    {icon}
                    <div className="flex flex-col gap-0.5 min-w-0 flex-1">
                        {titleLink ? (
                            <DsButton
                                variant="link"
                                size="sm"
                                className="self-start"
                                trailing={<ArrowShortUpRightIcon />}
                                nativeButton={false}
                                role="link"
                                render={
                                    <a
                                        href={`https://solscan.io/account/${titleLink}`}
                                        onClick={() =>
                                            productEvent(
                                                "rewards:link_clicked",
                                                {
                                                    entry_point: "position_row",
                                                    link_type: linkType
                                                }
                                            )
                                        }
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    />
                                }
                            >
                                {title}
                            </DsButton>
                        ) : (
                            <Text
                                variant="small"
                                render={<span />}
                                className="text-foreground-base min-w-0 truncate"
                            >
                                {title}
                            </Text>
                        )}
                        {destination}
                    </div>
                    <Text
                        variant="body"
                        render={<span />}
                        className="text-foreground-base tabular-nums shrink-0"
                    >
                        {amount}
                    </Text>
                </div>
                {editor}
            </div>
        </div>
    );
}
